import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getABTest, updateABTest, uploadABScreenshot, downloadFileBlob, getTeams, getBrands } from '@/lib/api';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Flask, Trophy, ArrowUp, ArrowDown, Minus, Image, LinkSimple,
  Clock, CalendarBlank, Users, Target, ChartBar, Lightbulb, CheckCircle, XCircle
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  'Not Started': '#E8E8E8', 'Running': '#B3D4FF', 'Completed': '#34C759', 'Implemented': '#10B981',
};

export default function ABTestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [brands, setBrands] = useState([]);
  const [varAUrl, setVarAUrl] = useState(null);
  const [varBUrl, setVarBUrl] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [testRes, teamsRes, brandsRes] = await Promise.all([
        getABTest(id), getTeams(), getBrands()
      ]);
      setTest(testRes.data);
      setTeams(teamsRes.data);
      setBrands(brandsRes.data);
      setEditForm(testRes.data);

      if (testRes.data.variant_a_screenshot) {
        try { setVarAUrl(await downloadFileBlob(testRes.data.variant_a_screenshot)); } catch (err) { console.error('Variant A screenshot error:', err.message); }
      }
      if (testRes.data.variant_b_screenshot) {
        try { setVarBUrl(await downloadFileBlob(testRes.data.variant_b_screenshot)); } catch (err) { console.error('Variant B screenshot error:', err.message); }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleScreenshot = async (variant) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        await uploadABScreenshot(id, variant, file);
        loadData();
      } catch (err) { console.error(err); }
    };
    input.click();
  };

  const handleSave = async () => {
    try {
      const payload = {};
      const fields = ['test_name', 'start_date', 'end_date', 'audience_split', 'target_audience', 'testing_domain', 'custom_domain', 'primary_metric', 'variant_a_result', 'variant_b_result', 'winner', 'confidence_level', 'decision', 'status', 'key_insights', 'observations', 'analytics_links'];
      fields.forEach(f => {
        if (editForm[f] !== test[f]) {
          let val = editForm[f];
          if ((f === 'variant_a_result' || f === 'variant_b_result') && val !== null && val !== '' && val !== undefined) {
            val = parseFloat(val);
            if (isNaN(val)) return;
          }
          if (val === '') val = null;
          payload[f] = val;
        }
      });
      if (Object.keys(payload).length > 0) {
        const res = await updateABTest(id, payload);
        setTest(res.data);
        setEditForm(res.data);
      }
      setEditing(false);
    } catch (e) { console.error(e); }
  };

  const updateField = (field) => (e) => setEditForm(prev => ({ ...prev, [field]: e.target.value }));

  if (loading) return <div className="p-8 text-[#555555]">Loading test...</div>;
  if (!test) return <div className="p-8 text-[#555555]">Test not found</div>;

  const teamMap = Object.fromEntries(teams.map(t => [t.id, t.name]));
  const brandMap = Object.fromEntries(brands.map(b => [b.id, b.name]));
  const pctChange = test.percent_change;

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="ab-test-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#555555] hover:text-[#0F0F0F] mb-5 transition-colors" data-testid="ab-back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (STATUS_COLORS[test.status] || '#E8E8E8') + '60' }}>
              {test.status}
            </span>
            {test.winner && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${test.winner === 'B' ? 'bg-[#32B4AC]/10 text-[#32B4AC]' : test.winner === 'A' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-[#FFB800]/10 text-[#FFB800]'}`}>
                Winner: {test.winner === 'Inconclusive' ? 'Inconclusive' : `Variant ${test.winner}`}
              </span>
            )}
            {test.confidence_level && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#E8E8E8] text-[#1A1A1A] font-medium">{test.confidence_level} Confidence</span>
            )}
          </div>
          <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F] flex items-center gap-2" data-testid="ab-test-name">
            <Flask size={24} weight="fill" className="text-[#32B4AC]" />
            {test.test_name}
          </h1>
          <button onClick={() => navigate(`/tasks/${test.linked_task_id}`)} className="text-sm text-[#32B4AC] hover:underline mt-1" data-testid="ab-linked-task-link">
            Linked Task: {test.linked_task_title}
          </button>
        </div>
        <div className="flex gap-2 shrink-0">
          {editing ? (
            <>
              <button onClick={handleSave} className="h-9 px-4 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94]" data-testid="ab-save-btn">Save</button>
              <button onClick={() => { setEditing(false); setEditForm(test); }} className="h-9 px-4 border border-[#E8E8E8] text-sm rounded-md hover:bg-[#F4F4F4]" data-testid="ab-cancel-btn">Cancel</button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="h-9 px-4 border border-[#1A1A1A] text-sm font-medium rounded-md hover:bg-[#F4F4F4]" data-testid="ab-edit-btn">Edit Test</button>
          )}
        </div>
      </div>

      {/* Key Metric Highlight */}
      {(test.variant_a_result != null || test.variant_b_result != null) && (
        <div className="border-2 border-[#32B4AC] rounded-md p-5 mb-6 bg-[#32B4AC]/5" data-testid="ab-metric-highlight">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#32B4AC] mb-3 flex items-center gap-2">
            <ChartBar size={14} /> {test.primary_metric} Comparison
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xs text-[#555555] mb-1">Variant A (Current)</p>
              <p className="font-['Manrope'] text-3xl font-bold text-[#0F0F0F]">{test.variant_a_result ?? '-'}</p>
            </div>
            <div className="text-center flex flex-col items-center justify-center">
              <p className="text-xs text-[#555555] mb-1">Change</p>
              {pctChange != null ? (
                <p className={`font-['Manrope'] text-3xl font-bold flex items-center gap-1 ${pctChange > 0 ? 'text-[#34C759]' : pctChange < 0 ? 'text-[#FF3B30]' : 'text-[#555555]'}`}>
                  {pctChange > 0 ? <ArrowUp size={20} /> : pctChange < 0 ? <ArrowDown size={20} /> : <Minus size={20} />}
                  {pctChange > 0 ? '+' : ''}{pctChange}%
                </p>
              ) : <p className="text-2xl text-[#E8E8E8]">-</p>}
            </div>
            <div className="text-center">
              <p className="text-xs text-[#555555] mb-1">Variant B (Proposed)</p>
              <p className="font-['Manrope'] text-3xl font-bold text-[#0F0F0F]">{test.variant_b_result ?? '-'}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visual Comparison */}
          <div data-testid="ab-visual-comparison">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Visual Comparison</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-[#E8E8E8] rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold text-[#FF3B30] uppercase tracking-wider">Variant A - Current</h4>
                  <button onClick={() => handleScreenshot('a')} className="text-xs text-[#32B4AC] hover:underline flex items-center gap-1" data-testid="upload-variant-a-btn">
                    <Image size={12} /> Upload
                  </button>
                </div>
                {varAUrl ? (
                  <img src={varAUrl} alt="Variant A" className="w-full rounded-md border border-[#E8E8E8]" data-testid="variant-a-image" />
                ) : <div className="h-48 bg-[#F4F4F4] rounded-md flex items-center justify-center text-xs text-[#555555]">No screenshot</div>}
              </div>
              <div className="border border-[#E8E8E8] rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold text-[#32B4AC] uppercase tracking-wider">Variant B - Proposed</h4>
                  <button onClick={() => handleScreenshot('b')} className="text-xs text-[#32B4AC] hover:underline flex items-center gap-1" data-testid="upload-variant-b-btn">
                    <Image size={12} /> Upload
                  </button>
                </div>
                {varBUrl ? (
                  <img src={varBUrl} alt="Variant B" className="w-full rounded-md border border-[#E8E8E8]" data-testid="variant-b-image" />
                ) : <div className="h-48 bg-[#F4F4F4] rounded-md flex items-center justify-center text-xs text-[#555555]">No screenshot</div>}
              </div>
            </div>
          </div>

          {/* Metrics (editable) */}
          {editing && (
            <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4" data-testid="ab-metrics-edit">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Metrics</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Primary Metric</label>
                  <Input data-testid="ab-primary-metric-input" value={editForm.primary_metric || ''} onChange={updateField('primary_metric')} className="h-8 text-xs border-[#E8E8E8] rounded-md" />
                </div>
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Variant A Result</label>
                  <Input data-testid="ab-variant-a-result-input" type="number" step="0.01" value={editForm.variant_a_result ?? ''} onChange={updateField('variant_a_result')} className="h-8 text-xs border-[#E8E8E8] rounded-md" />
                </div>
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Variant B Result</label>
                  <Input data-testid="ab-variant-b-result-input" type="number" step="0.01" value={editForm.variant_b_result ?? ''} onChange={updateField('variant_b_result')} className="h-8 text-xs border-[#E8E8E8] rounded-md" />
                </div>
              </div>
            </div>
          )}

          {/* Outcome (editable) */}
          {editing && (
            <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4" data-testid="ab-outcome-edit">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Outcome</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Winner</label>
                  <select data-testid="ab-winner-select" value={editForm.winner || ''} onChange={updateField('winner')} className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
                    <option value="">Not decided</option>
                    <option value="A">Variant A</option>
                    <option value="B">Variant B</option>
                    <option value="Inconclusive">Inconclusive</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Confidence</label>
                  <select data-testid="ab-confidence-select" value={editForm.confidence_level || ''} onChange={updateField('confidence_level')} className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
                    <option value="">Not set</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Decision</label>
                  <select data-testid="ab-decision-select" value={editForm.decision || ''} onChange={updateField('decision')} className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
                    <option value="">No decision</option>
                    <option value="Implement Variant A">Implement Variant A</option>
                    <option value="Implement Variant B">Implement Variant B</option>
                    <option value="Iterate Further">Iterate Further</option>
                    <option value="Rollback">Rollback</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Notes & Learnings */}
          <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4" data-testid="ab-notes-section">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] flex items-center gap-2"><Lightbulb size={14} /> Notes & Learnings</h3>
            {editing ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Key Insights</label>
                  <Textarea data-testid="ab-insights-input" value={editForm.key_insights || ''} onChange={updateField('key_insights')} placeholder="What did we learn?" className="min-h-[80px] text-sm border-[#E8E8E8] rounded-md resize-none" />
                </div>
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Observations</label>
                  <Textarea data-testid="ab-observations-input" value={editForm.observations || ''} onChange={updateField('observations')} placeholder="Any notable observations..." className="min-h-[60px] text-sm border-[#E8E8E8] rounded-md resize-none" />
                </div>
                <div>
                  <label className="text-xs text-[#555555] block mb-1">Analytics Links</label>
                  <Input data-testid="ab-analytics-links-input" value={editForm.analytics_links || ''} onChange={updateField('analytics_links')} placeholder="https://analytics.google.com/..." className="h-8 text-xs border-[#E8E8E8] rounded-md" />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {test.key_insights && (
                  <div>
                    <p className="text-xs font-medium text-[#555555] mb-1">Key Insights</p>
                    <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{test.key_insights}</p>
                  </div>
                )}
                {test.observations && (
                  <div>
                    <p className="text-xs font-medium text-[#555555] mb-1">Observations</p>
                    <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{test.observations}</p>
                  </div>
                )}
                {test.analytics_links && (
                  <a href={test.analytics_links} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#32B4AC] hover:underline" data-testid="ab-analytics-link">
                    <LinkSimple size={14} /> Analytics Dashboard
                  </a>
                )}
                {!test.key_insights && !test.observations && !test.analytics_links && (
                  <p className="text-xs text-[#555555]">No notes yet. Click "Edit Test" to add insights.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-5">
          {/* Status */}
          <div className="border border-[#E8E8E8] rounded-md p-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Status</h4>
            {editing ? (
              <select data-testid="ab-status-select" value={editForm.status || ''} onChange={updateField('status')} className="w-full h-9 text-sm border border-[#E8E8E8] rounded-md px-2 bg-white">
                <option value="Not Started">Not Started</option>
                <option value="Running">Running</option>
                <option value="Completed">Completed</option>
                <option value="Implemented">Implemented</option>
              </select>
            ) : (
              <span className="text-sm font-medium px-3 py-1 rounded-full inline-block" style={{ backgroundColor: (STATUS_COLORS[test.status] || '#E8E8E8') + '60' }}>
                {test.status}
              </span>
            )}
          </div>

          {/* Experiment Setup */}
          <div className="border border-[#E8E8E8] rounded-md p-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Experiment Setup</h4>
            <div className="space-y-3">
              {editing ? (
                <>
                  <div>
                    <label className="text-xs text-[#555555] block mb-1">Start Date</label>
                    <Input data-testid="ab-start-date-input" type="date" value={editForm.start_date ? editForm.start_date.split('T')[0] : ''} onChange={updateField('start_date')} className="h-8 text-xs border-[#E8E8E8] rounded-md" />
                  </div>
                  <div>
                    <label className="text-xs text-[#555555] block mb-1">End Date</label>
                    <Input data-testid="ab-end-date-input" type="date" value={editForm.end_date ? editForm.end_date.split('T')[0] : ''} onChange={updateField('end_date')} className="h-8 text-xs border-[#E8E8E8] rounded-md" />
                  </div>
                  <div>
                    <label className="text-xs text-[#555555] block mb-1">Audience Split</label>
                    <Input data-testid="ab-audience-split-input" value={editForm.audience_split || ''} onChange={updateField('audience_split')} placeholder="50:50" className="h-8 text-xs border-[#E8E8E8] rounded-md" />
                  </div>
                  <div>
                    <label className="text-xs text-[#555555] block mb-1">Target Audience</label>
                    <select data-testid="ab-target-audience-select" value={editForm.target_audience || ''} onChange={updateField('target_audience')} className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
                      <option value="All">All</option>
                      <option value="New users">New users</option>
                      <option value="Returning users">Returning users</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#555555] block mb-1">Testing Domain</label>
                    <select data-testid="ab-testing-domain-select" value={editForm.testing_domain || ''} onChange={updateField('testing_domain')} className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
                      <option value="wellversed.in">wellversed.in</option>
                      <option value="store.wellversed.in">store.wellversed.in</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {editForm.testing_domain === 'Other' && (
                    <div>
                      <label className="text-xs text-[#555555] block mb-1">Custom Domain</label>
                      <Input data-testid="ab-custom-domain-input" value={editForm.custom_domain || ''} onChange={updateField('custom_domain')} className="h-8 text-xs border-[#E8E8E8] rounded-md" />
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-2 text-xs">
                  <InfoRow icon={CalendarBlank} label="Start" value={test.start_date ? new Date(test.start_date).toLocaleDateString() : 'Not set'} />
                  <InfoRow icon={CalendarBlank} label="End" value={test.end_date ? new Date(test.end_date).toLocaleDateString() : 'Not set'} />
                  <InfoRow icon={Clock} label="Duration" value={test.duration_days != null ? `${test.duration_days} days` : 'N/A'} />
                  <InfoRow icon={Users} label="Split" value={test.audience_split || '50:50'} />
                  <InfoRow icon={Target} label="Audience" value={test.target_audience || 'All'} />
                  <InfoRow icon={LinkSimple} label="Domain" value={test.testing_domain === 'Other' ? (test.custom_domain || 'Other') : test.testing_domain} />
                </div>
              )}
            </div>
          </div>

          {/* Outcome summary (read-only) */}
          {!editing && (test.winner || test.decision) && (
            <div className="border border-[#E8E8E8] rounded-md p-4" data-testid="ab-outcome-summary">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Outcome</h4>
              <div className="space-y-2 text-xs">
                {test.winner && <InfoRow icon={Trophy} label="Winner" value={test.winner === 'Inconclusive' ? 'Inconclusive' : `Variant ${test.winner}`} highlight />}
                {test.confidence_level && <InfoRow icon={ChartBar} label="Confidence" value={test.confidence_level} />}
                {test.decision && <InfoRow icon={test.decision.includes('Rollback') ? XCircle : CheckCircle} label="Decision" value={test.decision} highlight />}
              </div>
            </div>
          )}

          {/* Meta */}
          <div className="border border-[#E8E8E8] rounded-md p-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Info</h4>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-[#555555]">Brand</span>
                <span className="text-[#0F0F0F] font-medium">{brandMap[test.brand_id] || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#555555]">Team</span>
                <span className="text-[#0F0F0F] font-medium">{teamMap[test.assigned_team] || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#555555]">Created by</span>
                <span className="text-[#0F0F0F] font-medium">{test.created_by_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#555555]">Created</span>
                <span className="text-[#0F0F0F]">{new Date(test.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, highlight }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="flex items-center gap-1.5 text-[#555555]"><Icon size={12} /> {label}</span>
      <span className={`font-medium ${highlight ? 'text-[#32B4AC]' : 'text-[#0F0F0F]'}`}>{value}</span>
    </div>
  );
}
